/**
 * Pure rules for the public client upload page (spec §2.4, R.7–R.10).
 *
 * Everything here is deterministic — no DOM, no network, no timers — so the
 * retry policy that keeps a 30-photo pick on a shared mobile IP from ever
 * showing a 429 (plan Review Focus #2) is covered by unit tests rather than
 * by hoping the page behaves.
 */

export type FileKind = 'photo' | 'video' | 'pdf';
export type QueueStatus = 'pending' | 'uploading' | 'done' | 'failed';

export const MB = 1024 * 1024;
/** Mirrors the server: `MAX_UPLOAD_BYTES` (photos) and `MAX_PUBLIC_VIDEO_BYTES`. */
export const PHOTO_LIMIT_BYTES = 25 * MB;
export const FILE_LIMIT_BYTES = 100 * MB;
/** The server's own 413 copy, so a client-side refusal reads the same. */
export const PHOTO_TOO_LARGE_MESSAGE = 'Photos must be under 25 MB';
export const FILE_TOO_LARGE_MESSAGE = 'Files must be under 100 MB';
export const TYPE_NOT_ACCEPTED_MESSAGE =
  "This type of file can't be sent. Please send photos, videos or PDFs.";
export const EMPTY_FILE_MESSAGE = 'This file is empty.';
export const RATE_LIMITED_MESSAGE = 'Network busy — tap to retry';
export const NETWORK_FAILED_MESSAGE = 'Connection lost — tap to retry';
export const SERVER_FAILED_MESSAGE = 'Something went wrong — tap to retry';
export const DEAD_LINK_MESSAGE =
  'This link is no longer active. Please ask your agency for a new one.';
export const UNREADABLE_FILE_MESSAGE =
  "Your phone didn't let us read this file — please choose it again";
export const MAX_MESSAGE_LENGTH = 1000;

/** R.9: a 429 is retried after 2 s, 4 s, 8 s, then shown as failed. */
export const RATE_LIMIT_DELAYS_MS: readonly number[] = [2000, 4000, 8000];
/** A dropped connection (or a 5xx) gets one automatic retry after 2 s. */
export const NETWORK_RETRY_DELAY_MS = 2000;
/** Upper bound on how long a server `Retry-After` may stretch one wait. */
export const MAX_RETRY_AFTER_MS = 60_000;

/** Must match the server's `ALLOWED_UPLOAD_MIME`. */
const MIME_KIND: Readonly<Record<string, FileKind>> = {
  'image/jpeg': 'photo',
  'image/png': 'photo',
  'image/heic': 'photo',
  'image/heif': 'photo',
  'image/webp': 'photo',
  'video/mp4': 'video',
  'video/quicktime': 'video',
  'application/pdf': 'pdf',
};

/**
 * Some phones and desktop browsers hand over HEIC/HEIF/MOV files with an empty
 * (or generic) MIME type. The server checks the multipart part's type, so the
 * page re-labels those from the extension before sending.
 */
const EXTENSION_MIME: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  heif: 'image/heif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  pdf: 'application/pdf',
};

/** Types a browser uses when it does not know what a file is. */
const UNKNOWN_MIME = new Set(['', 'application/octet-stream']);

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

/** The MIME type to send, or null when the file is not one the server accepts. */
export function resolveMime(name: string, type: string): string | null {
  const t = type.trim().toLowerCase();
  // Some Android builds report `image/jpg`.
  const normalised = t === 'image/jpg' ? 'image/jpeg' : t;
  if (normalised in MIME_KIND) return normalised;
  if (UNKNOWN_MIME.has(normalised)) return EXTENSION_MIME[extensionOf(name)] ?? null;
  return null;
}

export function kindOfMime(mime: string): FileKind {
  return MIME_KIND[mime] ?? 'pdf';
}

export type Validation =
  | { ok: true; kind: FileKind; mime: string }
  | { ok: false; reason: string; kind: FileKind | null };

/** Checked on pick, before any byte is sent (R.8). */
export function validateFile(file: { name: string; type: string; size: number }): Validation {
  const mime = resolveMime(file.name, file.type);
  if (mime === null) return { ok: false, reason: TYPE_NOT_ACCEPTED_MESSAGE, kind: null };
  const kind = kindOfMime(mime);
  if (file.size <= 0) return { ok: false, reason: EMPTY_FILE_MESSAGE, kind };
  if (kind === 'photo' && file.size > PHOTO_LIMIT_BYTES) {
    return { ok: false, reason: PHOTO_TOO_LARGE_MESSAGE, kind };
  }
  if (file.size > FILE_LIMIT_BYTES) return { ok: false, reason: FILE_TOO_LARGE_MESSAGE, kind };
  return { ok: true, kind, mime };
}

/** Every photo fits; only large videos and PDFs stay on the phone's handle. */
export const HOLD_IN_MEMORY_MAX_BYTES = PHOTO_LIMIT_BYTES;

/**
 * Copy a picked file's bytes into memory, while the phone is still sharing it.
 *
 * On Android a picked File is only a handle onto the gallery's copy. Once the
 * gallery touches that copy (a backup, a re-scan of a fresh camera shot) the
 * browser refuses to read it — the upload dies before a byte leaves the phone,
 * which XHR reports as a lost connection, and every retry fails the same way
 * until the client picks the photo again. Seen in production 2026-09-30:
 * failed rows never reached the server, and the same photos re-picked minutes
 * later on the same network all went through. A copy taken at pick time has
 * no handle to lose.
 *
 * Returns the original when it is too big to hold, and null when the phone
 * has already stopped sharing it.
 */
export async function holdFileBytes(
  file: File,
  maxBytes: number = HOLD_IN_MEMORY_MAX_BYTES,
): Promise<File | null> {
  if (file.size > maxBytes) return file;
  try {
    const bytes = await file.arrayBuffer();
    return new File([bytes], file.name, { type: file.type, lastModified: file.lastModified });
  } catch {
    return null;
  }
}

export interface QueueItem {
  /** The `uploadId` sent to the server — reused on every retry (R.7). */
  id: string;
  name: string;
  bytes: number;
  kind: FileKind | null;
  mime: string | null;
  status: QueueStatus;
  /** 0–100 while uploading. */
  progress: number;
  reason: string | null;
  /** False for files refused on pick: tapping them cannot help. */
  retryable: boolean;
  /** 429s received since the last manual retry. */
  rateLimitHits: number;
  /** Network / 5xx failures since the last manual retry. */
  networkFailures: number;
  /** A 409 has already been answered with a new id once. */
  conflictRetried: boolean;
  /** Epoch ms before which an automatic retry must not start. */
  retryAt: number | null;
}

export function createQueueItem(
  id: string,
  file: { name: string; type: string; size: number },
): QueueItem {
  const v = validateFile(file);
  return {
    id,
    name: file.name,
    bytes: file.size,
    kind: v.kind,
    mime: v.ok ? v.mime : null,
    status: v.ok ? 'pending' : 'failed',
    progress: 0,
    reason: v.ok ? null : v.reason,
    retryable: v.ok,
    rateLimitHits: 0,
    networkFailures: 0,
    conflictRetried: false,
    retryAt: null,
  };
}

/** The bytes could not be read at pick time: only picking it again can help. */
export function markUnreadable(item: QueueItem): QueueItem {
  return { ...item, status: 'failed', reason: UNREADABLE_FILE_MESSAGE, retryable: false };
}

export type NextStep =
  | { type: 'start'; id: string }
  | { type: 'wait'; ms: number }
  | { type: 'idle' };

/**
 * One at a time, in pick order. A file waiting out a backoff holds the queue:
 * sending the next file straight away would just earn another 429.
 */
export function selectNext(items: readonly QueueItem[], now: number): NextStep {
  if (items.some((i) => i.status === 'uploading')) return { type: 'idle' };
  const next = items.find((i) => i.status === 'pending');
  if (next === undefined) return { type: 'idle' };
  if (next.retryAt !== null && next.retryAt > now) return { type: 'wait', ms: next.retryAt - now };
  return { type: 'start', id: next.id };
}

export function markUploading(item: QueueItem): QueueItem {
  return { ...item, status: 'uploading', progress: 0, reason: null, retryAt: null };
}

export function markProgress(item: QueueItem, loaded: number, total: number): QueueItem {
  if (item.status !== 'uploading' || total <= 0) return item;
  // Hold at 99 until the server answers: 100% of bytes sent is not "Sent".
  const pct = Math.min(99, Math.max(0, Math.floor((loaded / total) * 100)));
  return pct > item.progress ? { ...item, progress: pct } : item;
}

export function markDone(item: QueueItem): QueueItem {
  return { ...item, status: 'done', progress: 100, reason: null, retryAt: null };
}

/** A tap on a failed row: same uploadId, fresh retry budget. */
export function manualRetry(item: QueueItem): QueueItem {
  if (item.status !== 'failed' || !item.retryable) return item;
  return {
    ...item,
    status: 'pending',
    progress: 0,
    reason: null,
    rateLimitHits: 0,
    networkFailures: 0,
    conflictRetried: false,
    retryAt: null,
  };
}

export type UploadOutcome =
  | { type: 'network' }
  | { type: 'http'; status: number; message?: string | null; retryAfterMs?: number | null };

/**
 * What the page must do beyond updating the row:
 * - `new-batch`: this visit's batchId belongs to another link — make a new one.
 * - `new-upload-id`: the item's uploadId was used elsewhere — give it a new one.
 * - `dead-link`: the link is revoked or unknown — stop the page.
 */
export type FailureEffect = 'none' | 'new-batch' | 'new-upload-id' | 'dead-link';

export interface FailureDecision {
  item: QueueItem;
  effect: FailureEffect;
}

function retryLater(item: QueueItem, now: number, delayMs: number): QueueItem {
  return { ...item, status: 'pending', progress: 0, reason: null, retryAt: now + delayMs };
}

function fail(item: QueueItem, reason: string, retryable = true): QueueItem {
  return { ...item, status: 'failed', progress: 0, reason, retryable, retryAt: null };
}

/** Friendlier copy for the server's 400s; its 413 copy is already written for people. */
function badRequestReason(message: string | null | undefined): string {
  const m = (message ?? '').trim();
  if (m === '' || /not accepted|upload rejected/i.test(m)) return TYPE_NOT_ACCEPTED_MESSAGE;
  if (/empty/i.test(m)) return EMPTY_FILE_MESSAGE;
  return m;
}

function afterTransientFailure(item: QueueItem, reason: string, now: number): FailureDecision {
  const failures = item.networkFailures + 1;
  const counted = { ...item, networkFailures: failures };
  if (failures <= 1) {
    return { item: retryLater(counted, now, NETWORK_RETRY_DELAY_MS), effect: 'none' };
  }
  return { item: fail(counted, reason), effect: 'none' };
}

function afterRateLimit(
  item: QueueItem,
  retryAfterMs: number | null | undefined,
  now: number,
): FailureDecision {
  const hits = item.rateLimitHits + 1;
  const counted = { ...item, rateLimitHits: hits };
  const base = RATE_LIMIT_DELAYS_MS[hits - 1];
  if (base === undefined) return { item: fail(counted, RATE_LIMITED_MESSAGE), effect: 'none' };
  const hinted = Math.min(retryAfterMs ?? 0, MAX_RETRY_AFTER_MS);
  return { item: retryLater(counted, now, Math.max(base, hinted)), effect: 'none' };
}

export function decideAfterFailure(
  item: QueueItem,
  outcome: UploadOutcome,
  now: number,
): FailureDecision {
  if (outcome.type === 'network' || outcome.status === 0) {
    return afterTransientFailure(item, NETWORK_FAILED_MESSAGE, now);
  }

  const { status } = outcome;

  if (status >= 500) return afterTransientFailure(item, SERVER_FAILED_MESSAGE, now);
  if (status === 429) return afterRateLimit(item, outcome.retryAfterMs, now);

  if (status === 413) {
    const m = (outcome.message ?? '').trim();
    const reason =
      m !== '' ? m : item.kind === 'photo' ? PHOTO_TOO_LARGE_MESSAGE : FILE_TOO_LARGE_MESSAGE;
    return { item: fail(item, reason, false), effect: 'none' };
  }

  if (status === 404 || status === 410) {
    return { item: fail(item, DEAD_LINK_MESSAGE, false), effect: 'dead-link' };
  }

  if (status === 409) {
    if (item.conflictRetried) return { item: fail(item, SERVER_FAILED_MESSAGE), effect: 'none' };
    const effect: FailureEffect = /upload id/i.test(outcome.message ?? '')
      ? 'new-upload-id'
      : 'new-batch';
    return {
      item: {
        ...item,
        status: 'pending',
        progress: 0,
        reason: null,
        retryAt: null,
        conflictRetried: true,
      },
      effect,
    };
  }

  if (status === 400 || status === 415 || status === 422) {
    return { item: fail(item, badRequestReason(outcome.message), false), effect: 'none' };
  }

  return { item: fail(item, SERVER_FAILED_MESSAGE), effect: 'none' };
}

/** `Retry-After` is seconds or an HTTP date. */
export function parseRetryAfter(header: string | null, now: number): number | null {
  if (header === null || header.trim() === '') return null;
  const secs = Number(header);
  if (Number.isFinite(secs)) return secs >= 0 ? Math.round(secs * 1000) : null;
  const at = Date.parse(header);
  return Number.isNaN(at) ? null : Math.max(0, at - now);
}

export function isBusy(items: readonly QueueItem[]): boolean {
  return items.some((i) => i.status === 'pending' || i.status === 'uploading');
}

export interface QueueSummary {
  total: number;
  sent: number;
  failed: number;
  busy: boolean;
  text: string;
}

export function summarise(items: readonly QueueItem[]): QueueSummary {
  const total = items.length;
  const sent = items.filter((i) => i.status === 'done').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  const busy = isBusy(items);
  let text = `${String(sent)} of ${String(total)} sent`;
  if (failed > 0) text += ` · ${String(failed)} not sent`;
  return { total, sent, failed, busy, text };
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < MB) return `${String(Math.round(bytes / 1024))} KB`;
  const mb = bytes / MB;
  return `${mb < 10 ? mb.toFixed(1) : String(Math.round(mb))} MB`;
}

/**
 * UUID v4. `crypto.randomUUID` only exists in secure contexts; the link may be
 * opened over plain http on a LAN during a demo, so fall back to
 * `getRandomValues`, which exists everywhere.
 */
export function uuidV4(fill: (bytes: Uint8Array) => Uint8Array): string {
  const b = fill(new Uint8Array(16));
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface SentFile {
  originalName: string | null;
  bytes: number | null;
  kind: string;
  receivedAt: string;
}

export function newestFirst(files: readonly SentFile[]): SentFile[] {
  return [...files].sort((a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt));
}
